import React, { useCallback, useState } from "react";
import { WORLDS, STATUS, slugify } from "../config/worlds";
import { AGENT_LAYOUT, BRIDGES } from "../config/layout";
import { SPRITES } from "../config/sprites";
import { useCouriers } from "../hooks/useCouriers";
import { useHandoffFeed } from "../hooks/useAgentEvents";
import leadsArt from "../assets/world-leads.jpg";
import clientsArt from "../assets/world-clients.jpg";
import internalArt from "../assets/world-internal.jpg";

const ART = { leads: leadsArt, clients: clientsArt, internal: internalArt };

/**
 * One world map: art + house hotspots + drawn bridges + couriers.
 * Position comes from AGENT_LAYOUT (by agent id); live state from `agents`.
 */
export default function WorldMap({ db, world, agents, selectedId, onSelectAgent }) {
  const [pulsing, setPulsing] = useState({});

  const handleDeliver = useCallback((toId) => {
    setPulsing((p) => ({ ...p, [toId]: Date.now() }));
    setTimeout(() => setPulsing((p) => { const q = { ...p }; delete q[toId]; return q; }), 800);
  }, []);

  const { couriers, dispatch } = useCouriers(world, handleDeliver);
  useHandoffFeed(db, dispatch);

  const layout = AGENT_LAYOUT[world] ?? {};
  const bridge = BRIDGES[world];
  const color = WORLDS[world].color;
  const sprites = SPRITES[world] ?? {};
  const walkingFrom = new Set(couriers.map((c) => c.from));

  return (
    <div className="absolute inset-0 grid place-items-center overflow-hidden">
      <div className="relative w-[min(100%,calc(100vh*1.3333))] aspect-[1448/1086]">
        <img src={ART[world]} alt={`${WORLDS[world].name} map`} className="absolute inset-0 h-full w-full select-none" draggable={false} />

        {/* drawn bridge overlay (lead-to-client transition on the clients map) */}
        {bridge && (
          <>
            <svg viewBox="0 0 1448 1086" className="pointer-events-none absolute inset-0 h-full w-full">
              <path d={bridge.d} fill="none" stroke="#4c3319" strokeWidth="34" strokeLinecap="round" opacity=".9" />
              <path d={bridge.d} fill="none" stroke="#8B5A2B" strokeWidth="28" strokeLinecap="round" />
              <path d={bridge.d} fill="none" stroke="#C68B4E" strokeWidth="20" strokeDasharray="9 7" />
              <path d={bridge.ropeTop} fill="none" stroke="#5d3f22" strokeWidth="4" />
              <path d={bridge.ropeBottom} fill="none" stroke="#5d3f22" strokeWidth="4" />
            </svg>
            <div
              className="pointer-events-none absolute z-[5] -rotate-3 rounded-lg border-[2.5px] border-[#8B5A2B] bg-[#F3E4C2] px-2.5 py-0.5 font-display text-[12.5px] font-semibold text-[#4c3319] shadow-md"
              style={{ left: `${bridge.sign.left}%`, top: `${bridge.sign.top}%` }}
            >
              {bridge.sign.text}
            </div>
          </>
        )}

        {/* houses */}
        {agents
          .filter((a) => a.world === world)
          .map((a) => {
            const pos = layout[a.id] ?? layout[slugify(a.name)];
            if (!pos) return null;
            return (
              <House
                key={a.id}
                agent={a}
                pos={pos}
                selected={selectedId === a.id}
                pulsing={Boolean(pulsing[a.id])}
                onClick={() => onSelectAgent(a.id)}
              />
            );
          })}

        {/* standing residents (worlds with character sprites) */}
        {agents
          .filter((a) => a.world === world && sprites[a.id])
          .map((a) => {
            const pos = layout[a.id];
            if (!pos) return null;
            const mgr = pos.manager;
            return (
              <img
                key={`res-${a.id}`}
                src={sprites[a.id]}
                alt=""
                className={`pointer-events-none absolute z-[5] h-[8.2%] w-auto -translate-x-1/2 -translate-y-full animate-idle-bob drop-shadow-md transition-opacity duration-500 ${
                  walkingFrom.has(a.id) ? "opacity-0" : "opacity-100"
                }`}
                style={{ left: `${pos.x + (mgr ? 5.8 : 4.3)}%`, top: `${pos.y + (mgr ? 7.5 : 5.4)}%` }}
              />
            );
          })}

        {/* couriers */}
        {couriers.map((c) => {
          const sprite = sprites[c.from];
          return (
            <div
              key={c.id}
              className={`group pointer-events-auto absolute z-[7] flex flex-col items-center gap-px drop-shadow-md ${
                sprite ? "-translate-x-1/2 -translate-y-[96%]" : "-translate-x-1/2 -translate-y-[90%]"
              }`}
              style={{ left: `${c.x}%`, top: `${c.y}%` }}
            >
              <span className="absolute -top-5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md border border-white/25 bg-[#122A4A]/90 px-2 py-px font-display text-[10px] font-semibold text-white opacity-0 transition group-hover:opacity-100">
                {c.label}
              </span>
              {sprite ? (
                <>
                  <span className="absolute -right-3 -top-3.5 grid h-[22px] w-[22px] place-items-center rounded-full border-2 border-[#3c2814]/35 bg-white/95 text-[13px] leading-none">
                    {c.item}
                  </span>
                  <img src={sprite} alt="" className="h-[58px] w-auto animate-sprite-walk" />
                </>
              ) : (
                <>
                  <span className="grid h-6 w-6 animate-item-bob place-items-center rounded-full border-2 border-[#3c2814]/35 bg-white/95 text-[15px] leading-none">
                    {c.item}
                  </span>
                  <span className="relative h-[17px] w-[17px] animate-walk-bob rounded-full border-[2.5px] border-white" style={{ background: color }}>
                    <span className="absolute left-[3px] top-1 h-[3.5px] w-[3.5px] rounded-full bg-white" />
                    <span className="absolute right-[3px] top-1 h-[3.5px] w-[3.5px] rounded-full bg-white" />
                  </span>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function House({ agent, pos, selected, pulsing, onClick }) {
  const st = agent.status;
  const manager = pos.manager;
  return (
    <button
      type="button"
      aria-label={agent.name}
      onClick={onClick}
      className={`group absolute z-[6] -translate-x-1/2 -translate-y-1/2 rounded-2xl transition duration-200 hover:z-[8] hover:scale-105 ${
        manager ? "h-[15%] w-[12%]" : "h-[13.5%] w-[9.5%]"
      } ${pulsing ? "animate-house-pulse" : ""}`}
      style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
      data-status={st}
    >
      <span
        className={`pointer-events-none absolute -inset-[3px] rounded-[18px] border-[3px] transition duration-200 ${
          selected
            ? "border-amber-400 shadow-[0_0_22px_4px_rgba(245,185,18,.6)]"
            : pulsing
              ? "border-white/90 shadow-[0_0_22px_6px_rgba(255,255,255,.55)]"
              : "border-transparent group-hover:border-white/90 group-hover:shadow-[0_0_22px_4px_rgba(255,255,255,.5)]"
        }`}
      />

      {/* status dot (hidden when a bubble is showing) */}
      {st !== STATUS.APPROVE && st !== STATUS.BLOCKED && (
        <span
          className={`absolute right-[10%] top-[4%] h-[15px] w-[15px] rounded-full border-[2.5px] border-white shadow ${
            st === STATUS.WORKING
              ? "animate-pulse bg-emerald-400 shadow-emerald-400/60"
              : st === STATUS.OFFLINE
                ? "border-white/60 bg-slate-600/80"
                : "bg-slate-400"
          }`}
        />
      )}

      {/* needs-you bubble */}
      {(st === STATUS.APPROVE || st === STATUS.BLOCKED) && (
        <span
          className={`absolute -top-3 left-1/2 flex h-7 w-7 -translate-x-1/2 animate-bub items-center justify-center rounded-full border-[2.5px] border-white font-display text-[17px] font-bold shadow-lg ${
            st === STATUS.BLOCKED ? "bg-rose-500 text-white" : "bg-amber-400 text-amber-950"
          }`}
        >
          !
        </span>
      )}

      {manager ? (
        <span className="absolute left-1/2 top-[8%] flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full border-2 border-amber-400 bg-[#122A4A]/90 px-3 py-1 font-display text-[12.5px] font-semibold text-white shadow-lg">
          ⭐ {agent.name}
        </span>
      ) : (
        <span className="pointer-events-none absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-lg border-[1.5px] border-white/20 bg-[#122A4A]/90 px-2 py-0.5 font-display text-[11px] font-semibold text-white opacity-0 transition group-hover:opacity-100">
          {agent.name}
        </span>
      )}
    </button>
  );
}
